import BrandLogo from '../components/BrandLogo';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Sparkles, Crown, Calendar, TrendingUp, Users, CheckCircle2, ArrowRight, Clock } from 'lucide-react';
import { authManager } from '../services/authManager';

export default function CompletePurchasePage() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const user = authManager.getCurrentUser();
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const profile = await authManager.getProfile();
        setUserData(profile);
        
        // Si ya pagó, redirigir a la app
        if (profile.subscription && profile.subscription.paid_at) {
          navigate('/app');
        }
      } catch (error) {
        console.error('Error cargando perfil:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-morado/5">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-morado border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-morado/5">
      {/* Header con logo */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo className="w-40 sm:w-56" />
          </div>
          <button
            onClick={() => authManager.logout()}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Cerrar Sesión
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          {/* Ícono principal */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-morado flex items-center justify-center shadow-2xl">
              <Lock className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Título */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            ¡Un paso más para desbloquear <br className="hidden md:block" />
            <span className="text-morado">
              tu calendario numerológico
            </span>
          </h1>

          {/* Subtítulo */}
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            {userData?.user?.name ? `Hola ${userData.user.name}, ` : 'Hola, '}
            completaste el registro pero aún no has activado tu cuenta. <br />
            Activa tu plan ahora y empieza a transformar tu vida.
          </p>

          {/* Badge de urgencia */}
          <div className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-yellow-100 border border-yellow-300 rounded-full">
            <Clock className="w-4 h-4 text-yellow-700" />
            <span className="text-sm font-semibold text-yellow-700">
              Tu cuenta está en espera de activación
            </span>
          </div>
        </motion.div>

        {/* Beneficios destacados */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-8"
        >
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Lo que obtendrás al activar tu cuenta
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Calendar,
                title: 'Calendario Numerológico Completo',
                description: 'Descubre tus energías diarias y planifica con sabiduría ancestral'
              },
              {
                icon: Sparkles,
                title: 'Mensajes Personalizados Diarios',
                description: 'Guía personalizada en amor, salud, trabajo y finanzas cada día'
              },
              {
                icon: TrendingUp,
                title: 'Sincronización con tu Calendario',
                description: 'Integra tus energías numerológicas con Google, Apple y Outlook'
              },
              {
                icon: Users,
                title: 'Perfiles Ninja Adicionales',
                description: 'Consulta la numerología de familiares y clientes (según tu plan)'
              }
            ].map((benefit, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-4 rounded-xl hover:bg-morado/5 transition"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-morado flex items-center justify-center">
                  <benefit.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <button
            onClick={() => navigate('/pricing')}
            className="inline-flex items-center gap-3 px-8 py-4 bg-morado text-white text-lg font-semibold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            <Crown className="w-6 h-6" />
            Ver Planes y Activar Mi Cuenta
            <ArrowRight className="w-6 h-6" />
          </button>

          <p className="mt-4 text-sm text-gray-500">
            Elige el plan perfecto para ti y activa tu cuenta en minutos
          </p>
        </motion.div>

        {/* Social Proof */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-2 text-sm text-gray-600">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span>Miles de usuarios ya transformaron su vida con Numerathum</span>
          </div>
        </motion.div>

        {/* Footer info */}
        <div className="mt-12 text-center text-xs text-gray-500">
          <p>¿Necesitas ayuda? Contáctanos en <a href="mailto:soporte@numerathum.com" className="text-morado hover:underline">soporte@numerathum.com</a></p>
        </div>
      </div>
    </div>
  );
}

